import React, { memo, useState } from 'react';
import { useServiceWorker } from '../../hooks/useServiceWorker';

// ============================================
// Types
// ============================================

interface UpdatePromptProps {
  className?: string;
}

// ============================================
// Icons
// ============================================

const RefreshIcon = ({ spinning = false }: { spinning?: boolean }) => (
  <svg
    className={`w-4 h-4 ${spinning ? 'animate-spin' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

// ============================================
// Component
// ============================================

export const UpdatePrompt: React.FC<UpdatePromptProps> = memo(({ className = '' }) => {
  const { hasUpdate, skipWaiting } = useServiceWorker();
  const [dismissed, setDismissed] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  if (!hasUpdate || dismissed) {
    return null;
  }

  const handleUpdate = () => {
    setIsRefreshing(true);
    skipWaiting();

    // Fallback: if page doesn't reload within 3 seconds, force reload
    setTimeout(() => {
      console.log('[UpdatePrompt] Fallback reload triggered');
      window.location.reload();
    }, 3000);
  };

  const handleDismiss = () => {
    setDismissed(true);
  };

  return (
    <div
      className={`
        fixed bottom-4 right-4 z-50
        bg-white rounded-2xl shadow-2xl border border-slate-200
        p-4 max-w-sm
        animate-in slide-in-from-bottom-4
        ${className}
      `}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-xl ${isRefreshing ? 'bg-green-100' : 'bg-indigo-100'}`}>
          <RefreshIcon spinning={isRefreshing} />
        </div>

        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-bold text-slate-900">
            {isRefreshing ? 'Updating...' : 'Update Available'}
          </h4>
          <p className="text-xs text-slate-500 mt-1">
            {isRefreshing
              ? 'Please wait while we refresh the app with the latest version.'
              : 'A new version of OB WorkFlow is ready. Refresh to get the latest features.'
            }
          </p>

          {!isRefreshing && (
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={handleUpdate}
                className="px-4 py-2 bg-indigo-600 text-white text-xs font-bold rounded-xl hover:bg-indigo-700 transition-colors"
              >
                Refresh Now
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 text-slate-500 text-xs font-bold hover:text-slate-700 transition-colors"
              >
                Later
              </button>
            </div>
          )}

          {isRefreshing && (
            <div className="mt-3">
              <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                <div className="bg-indigo-600 h-1.5 rounded-full animate-pulse w-full"></div>
              </div>
            </div>
          )}
        </div>

        {!isRefreshing && (
          <button
            onClick={handleDismiss}
            className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Dismiss"
          >
            <CloseIcon />
          </button>
        )}
      </div>
    </div>
  );
});

UpdatePrompt.displayName = 'UpdatePrompt';

export default UpdatePrompt;
